import { useHotkey } from "@tanstack/react-hotkeys"

import HeaderTabs from "~/components/reusable/HeaderTabs"
import { useIsCoveredByFullPageDialog } from "~/components/reusable/dialogs/fullPageDialogLayer"
import { HOTKEYS } from "~/constants/hotkeys"
import { isEditableElement } from "~/utils/helpers"
import { useInspectorTabBar } from "~/components/design-system/pages/internal/inspectorTab"

/**
 * The inspector's tab row: the Module header's bar in CONTROLLED mode, since an inspector tab is
 * not a route. Drawn by the inspector chrome, so it stays pinned through a drilldown. Renders
 * nothing when the page registered no tabs.
 */
const InspectorTabs = () => {
  const { tabs, activeTabId, setActiveTabId, arrowKeys } = useInspectorTabBar()
  const isCoveredByDialog = useIsCoveredByFullPageDialog()
  const stepEnabled = arrowKeys && tabs.length > 1 && !!activeTabId && !isCoveredByDialog
  const step = (delta: 1 | -1) => (event: KeyboardEvent) => {
    if (isEditableElement(document.activeElement)) return
    const index = tabs.findIndex((t) => t.id === activeTabId)
    const next = tabs[index + delta]
    if (!next) return
    event.preventDefault()
    setActiveTabId(next.id)
  }
  useHotkey(HOTKEYS.prevInspectorTab.hotkey, step(-1), { enabled: stepEnabled })
  useHotkey(HOTKEYS.nextInspectorTab.hotkey, step(1), { enabled: stepEnabled })

  if (tabs.length === 0 || !activeTabId) return null

  const active = tabs.find((t) => t.id === activeTabId) ?? tabs[0]
  if (!active) return null

  return (
    <HeaderTabs
      tabs={tabs.map((t) => ({ label: t.label, icon: t.icon }))}
      selectedTab={active.label}
      onChange={(label) => {
        const next = tabs.find((t) => t.label === label)
        if (next) setActiveTabId(next.id)
      }}
      // Leave `HeaderTabs`' own px-4 alone: the indicator's transform subtracts a hardcoded 16px,
      // so changing it slides the underline off its tab.
      containerClassName="gap-4"
    />
  )
}

export default InspectorTabs
